import type { RtdbClient } from '@akpl/firebase/client';
import { DEFAULT_TONE, rtdbPaths, type ToneName, type TvPlaybackState } from '@akpl/shared';

import { APP_VERSION } from '../api';
import { getLiveClient } from './client';

/** docs/04 §4.4: the TV reports its state "throttled to 1/s". */
export const TV_STATE_THROTTLE_MS = 1000;

/** What a caller (the player) knows; `updatedAt` and `appVersion` are filled in here. */
export interface TvStateInput {
  state: TvPlaybackState;
  currentItemId?: string | null;
  positionMs?: number;
  durationMs?: number;
  tone?: ToneName;
}

interface Pending {
  sid: string;
  input: TvStateInput;
}

let lastWriteAt = 0;
let pending: Pending | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;
let onWriteError: ((error: Error) => void) | undefined;

function payload(input: TvStateInput, now: number): Record<string, unknown> {
  const value: Record<string, unknown> = {
    state: input.state,
    positionMs: Math.max(0, Math.round(input.positionMs ?? 0)),
    durationMs: Math.max(0, Math.round(input.durationMs ?? 0)),
    tone: input.tone ?? DEFAULT_TONE,
    updatedAt: now,
    appVersion: APP_VERSION,
  };
  // `.validate` rejects a null `currentItemId` (it must be a string when present), so omit it.
  if (input.currentItemId) value.currentItemId = input.currentItemId;
  return value;
}

function flushNow(client: RtdbClient, next: Pending, now: number): Promise<void> {
  lastWriteAt = now;
  pending = null;
  return client.write(rtdbPaths.tvState(next.sid), payload(next.input, now)).catch((error) => {
    // A rejected heartbeat must never break playback; the next tick tries again.
    onWriteError?.(error instanceof Error ? error : new Error(String(error)));
  });
}

/**
 * Writes `/sessions/{sid}/tvState` directly (the TV token is the only client allowed to,
 * docs/02 §2), throttled to one write per second: the first call goes out immediately and
 * further calls inside the window are coalesced into a single trailing write carrying the
 * newest values, so a 250 ms `timeupdate` loop still costs 1 write/s.
 *
 * Resolves when the write has been issued (or dropped into the trailing slot); it never
 * rejects — RTDB failures are reported through `setTvStateErrorHandler`.
 */
export function writeTvState(sid: string, state: TvStateInput): Promise<void> {
  const client = getLiveClient();
  if (!client) return Promise.resolve();

  const now = Date.now();
  const next: Pending = { sid, input: state };
  const elapsed = now - lastWriteAt;

  if (elapsed >= TV_STATE_THROTTLE_MS) {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
    return flushNow(client, next, now);
  }

  pending = next;
  if (timer === undefined) {
    timer = setTimeout(() => {
      timer = undefined;
      const queued = pending;
      if (!queued) return;
      const live = getLiveClient();
      if (live) void flushNow(live, queued, Date.now());
    }, TV_STATE_THROTTLE_MS - elapsed);
  }
  return Promise.resolve();
}

/** Writes the coalesced state right away (leaving the player, session close). */
export function flushTvState(): Promise<void> {
  if (timer !== undefined) {
    clearTimeout(timer);
    timer = undefined;
  }
  const queued = pending;
  const client = getLiveClient();
  if (!queued || !client) {
    pending = null;
    return Promise.resolve();
  }
  return flushNow(client, queued, Date.now());
}

export function setTvStateErrorHandler(handler: ((error: Error) => void) | undefined): void {
  onWriteError = handler;
}

/** Drops the throttle window and any pending write (sign-out, tests). */
export function resetTvState(): void {
  if (timer !== undefined) clearTimeout(timer);
  timer = undefined;
  pending = null;
  lastWriteAt = 0;
}
